import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Mail, Loader2, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '@/components/AuthProvider';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";

interface EmailConfirmationScreenProps {
  email: string;
  onBackToSignIn: () => void;
  onVerificationSuccess?: () => void;
}

export const EmailConfirmationScreen: React.FC<EmailConfirmationScreenProps> = ({
  email,
  onBackToSignIn,
  onVerificationSuccess,
}) => {
  const { resendConfirmationEmail } = useAuth();
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationError, setVerificationError] = useState('');

  const handleResendEmail = async () => {
    setIsResending(true);
    setResendSuccess(false);
    
    const { error } = await resendConfirmationEmail(email);
    
    if (error) {
      toast({
        title: "Failed to Resend",
        description: error.message,
        variant: "destructive",
      });
    } else {
      setResendSuccess(true);
      toast({
        title: "Email Sent!",
        description: "Check your inbox for the new verification code.",
      });
    }
    
    setIsResending(false);
  };

  const handleVerifyOtp = async (codeToVerify?: string) => {
    const code = codeToVerify || otpCode;
    if (code.length !== 6) {
      setVerificationError('Please enter the complete 6-digit code');
      return;
    }

    setIsVerifying(true);
    setVerificationError('');

    try {
      const { error } = await supabase.auth.verifyOtp({
        email: email,
        token: code,
        type: 'signup'
      });

      if (error) {
        setVerificationError(error.message);
        toast({
          title: "Verification Failed",
          description: error.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Email Verified!",
          description: "Your account is now active. Redirecting...",
        });
        
        // Call success callback or redirect to sign in
        if (onVerificationSuccess) {
          onVerificationSuccess();
        } else {
          onBackToSignIn();
        }
      }
    } catch (err: any) {
      setVerificationError(err.message || 'Verification failed');
      toast({
        title: "Verification Failed",
        description: err.message || 'An unexpected error occurred',
        variant: "destructive",
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const handleOtpChange = (value: string) => {
    setOtpCode(value);
    setVerificationError('');
    
    // Auto-submit when all 6 digits are entered
    if (value.length === 6) {
      setTimeout(() => {
        handleVerifyOtp(value);
      }, 100);
    }
  };

  return (
    <div className="text-center space-y-6 py-6">
      {/* Icon */}
      <div className="mx-auto w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center">
        <Mail className="h-10 w-10 text-primary" />
      </div>
      
      {/* Title */}
      <div className="space-y-2">
        <h3 className="text-xl font-semibold text-primary">Check Your Email</h3>
        <p className="text-sm text-muted-foreground">
          We sent a 6-digit code to:
        </p>
        <p className="font-medium text-primary">{email}</p>
      </div>
      
      {/* OTP Input */}
      <div className="space-y-6">
        <p className="text-sm font-medium text-foreground">Enter your verification code:</p>
        <div className="flex justify-center">
          <InputOTP 
            maxLength={6} 
            value={otpCode} 
            onChange={handleOtpChange}
            disabled={isVerifying}
          >
            <InputOTPGroup>
              <InputOTPSlot index={0} />
              <InputOTPSlot index={1} />
              <InputOTPSlot index={2} />
              <InputOTPSlot index={3} />
              <InputOTPSlot index={4} />
              <InputOTPSlot index={5} />
            </InputOTPGroup>
          </InputOTP>
        </div>
        
        {verificationError && (
          <div className="flex items-center justify-center gap-2 text-destructive text-sm">
            <AlertCircle className="h-4 w-4" />
            <span>{verificationError}</span>
          </div>
        )}

        <Button
          onClick={() => handleVerifyOtp()}
          disabled={otpCode.length !== 6 || isVerifying}
          className="w-full bg-primary hover:bg-primary/90 text-white"
        >
          {isVerifying ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Verifying...
            </>
          ) : (
            "Verify Code"
          )}
        </Button>
      </div>
      
      
      {/* Instructions */}
      <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground">
        <p>Don't see the email? Check your spam folder.</p>
      </div>
      
      {/* Actions */}
      <div className="space-y-3">
        <Button
          variant="outline"
          onClick={handleResendEmail}
          disabled={isResending}
          className="w-full bg-white/60 border-primary/30 text-primary hover:bg-primary/10"
        >
          {isResending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Sending...
            </>
          ) : resendSuccess ? (
            <>
              <CheckCircle className="mr-2 h-4 w-4 text-green-500" />
              Email Sent!
            </>
          ) : (
            <>
              <Mail className="mr-2 h-4 w-4" />
              Resend Code
            </>
          )}
        </Button>
        
        <Button
          variant="ghost"
          onClick={onBackToSignIn}
          className="w-full text-primary hover:bg-primary/10"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Sign In
        </Button>
      </div>
    </div>
  );
};
